import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material'

interface BreakdownItem {
  filterValue: string
  selectedCount: number
  totalCount: number
  percentage: number
}

interface PercentageBreakdownTableProps {
  breakdown: BreakdownItem[]
  title?: string
  filterFieldName?: string
}

export const PercentageBreakdownTable: React.FC<PercentageBreakdownTableProps> = ({
  breakdown,
  title,
  filterFieldName,
}) => {
  const displayTitle =
    title || (filterFieldName ? `Breakdown by ${filterFieldName}` : 'Breakdown by Filter')
  if (!breakdown || breakdown.length === 0) {
    return null
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ p: 1, fontWeight: 'bold', display: 'block' }}>
        {displayTitle}
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 300,
          boxShadow: 'none',
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: '0 4px',
          },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: '10px',
                  fontWeight: 'bold',
                  backgroundColor: '#fff',
                  borderBottom: '1px solid #dedede',
                  padding: '4px 10px',
                  top: 0,
                  zIndex: 2,
                }}
              >
                {filterFieldName || 'Filter Value'}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: '10px',
                  fontWeight: 'bold',
                  backgroundColor: '#fff',
                  borderBottom: '1px solid #dedede',
                  padding: '4px 10px',
                  top: 0,
                  zIndex: 2,
                }}
              >
                Selected
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: '10px',
                  fontWeight: 'bold',
                  backgroundColor: '#fff',
                  borderBottom: '1px solid #dedede',
                  padding: '4px 10px',
                  top: 0,
                  zIndex: 2,
                }}
              >
                Total
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: '10px',
                  fontWeight: 'bold',
                  backgroundColor: '#fff',
                  borderBottom: '1px solid #dedede',
                  padding: '4px 10px',
                  top: 0,
                  zIndex: 2,
                }}
              >
                Percentage
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {breakdown.map((item) => (
              <TableRow
                key={`${item.filterValue}-${item.selectedCount}-${item.totalCount}`}
                sx={{
                  backgroundColor: '#F6F6F6',
                  borderRadius: '4px',
                  '& td': {
                    borderBottom: 'none',
                  },
                  '& td:first-of-type': {
                    borderTopLeftRadius: '4px',
                    borderBottomLeftRadius: '4px',
                  },
                  '& td:last-of-type': {
                    borderTopRightRadius: '4px',
                    borderBottomRightRadius: '4px',
                  },
                }}
              >
                <TableCell sx={{ fontSize: '10px', padding: '8px 10px' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={item.filterValue}
                  >
                    {item.filterValue}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '10px', padding: '8px 10px' }}>
                  {item.selectedCount.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '10px', padding: '8px 10px' }}>
                  {item.totalCount.toLocaleString()}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: '10px', fontWeight: 'bold', padding: '8px 10px' }}
                >
                  {item.percentage.toFixed(1)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default PercentageBreakdownTable
